import { ActionConfirmation, usePublishActionOutcome } from "../ui/ActionConfirmation.js";
import { useRef, useState } from "react";
import { get, type Tutor } from "../api.js";
import { useAuth } from "../auth.js";
import { useAsync } from "../useAsync.js";
import { DrawerAddCredits } from "../calendar/DrawerAddCredits.js";
import { SellLessonBlock } from "./PaymentOperations.js";
import { blockSaleForm, paymentWorkflowAccess } from "./paymentWorkflow.js";
import {
  registrationSettledSummary,
  registrationSettlementWarning,
  type RegistrationSettledSummary,
} from "./registrationReconciliation.js";
import { staffBooking } from "./staff.js";

/** Entry-point adapter only: registration and accepted-write recovery stay in the existing forms. */
export function BlockSaleWorkflow({ canManage = true, ...props }: {
  studentId: string; studentName: string; providerId?: number | null; serviceName?: string | null;
  /** The host's appointment authority; see blockSaleForm. Log payment has no appointment. */
  canManage?: boolean;
  onCancel: () => void; onChanged: () => void; onBusyChange?: (busy: boolean) => void;
}) {
  const { me } = useAuth();
  const form = blockSaleForm(me, { canManage });
  if (form === null) return <p>You do not have access to lesson block sales.</p>;
  if (form === "five-credit") {
    return <DrawerAddCredits {...props} providerId={me!.booking.providerId!}
      onBusyChange={props.onBusyChange ?? (() => {})} />;
  }
  return <ManagedBlockSale {...props} />;
}

function ManagedBlockSale(props: Parameters<typeof BlockSaleWorkflow>[0]) {
  const publish = usePublishActionOutcome();
  const { me } = useAuth();
  const access = paymentWorkflowAccess(me);
  const settledSummary = useRef<ReturnType<typeof registrationSettledSummary>>(null);
  const [result, setResult] = useState<{
    description: string; balance: number; warning: string | null; settled: RegistrationSettledSummary | null;
  } | null>(null);
  const data = useAsync(async () => {
    const [products, tutors] = await Promise.all([
      staffBooking.lessonBlockProducts(), get<{ tutors: Tutor[] }>("/admin/tutors"),
    ]);
    return { products, tutors: tutors.tutors };
  }, []);
  if (data.loading) return <><p role="status">Loading configured lesson blocks…</p><button className="quiet" onClick={props.onCancel}>Cancel</button></>;
  if (data.error) return <><p role="alert">{data.error}</p><button className="quiet" onClick={props.onCancel}>Cancel</button></>;
  if (!data.data) return null;
  // Laid out as DrawerAddCredits lays out its summary, including the count of
  // settled lessons beyond the listed few.
  if (result) return <ActionConfirmation variant={result.warning ? "attention" : "success"} title="Lesson block added" description={props.studentName} details={[...(Number.isFinite(result.balance) ? [{ label: "New balance", value: `${result.balance} credits` }] : []), { value: <>
    <p>{result.description}</p>
    {result.warning && <p>{result.warning}</p>}
    {result.settled && <>
      <p><strong>{result.settled.heading}</strong></p>
      <ul>{result.settled.lessons.map((lesson, index) => <li key={index}>{lesson}</li>)}</ul>
      {result.settled.more > 0 && <p>and {result.settled.more} more</p>}
    </>}
    <button onClick={() => {
      publish({ variant: result.warning ? "attention" : "success", title: "Lesson block added", description: props.studentName,
        details: [{ value: result.description }, ...(Number.isFinite(result.balance) ? [{ label: "New balance", value: `${result.balance} credits` }] : []), ...(result.warning ? [{ value: result.warning }] : []), ...(result.settled ? [{ value: <>{result.settled.heading}<ul>{result.settled.lessons.map((lesson, index) => <li key={index}>{lesson}</li>)}</ul>{result.settled.more > 0 && <p>and {result.settled.more} more</p>}</> }] : [])] });
      props.onChanged();
    }}>Done</button>
  </> }]} />;
  return <SellLessonBlock studentId={props.studentId} studentName={props.studentName}
    canSelectProvider={access.manageAll} ownProviderId={access.ownProviderId}
    initialProviderId={props.providerId} initialServiceName={props.serviceName}
    tutors={data.data.tutors} products={data.data.products} onCancel={props.onCancel}
    onApply={async (mutation, description) => {
      props.onBusyChange?.(true);
      try {
        const detail = await mutation();
        const warning = registrationSettlementWarning(detail);
        settledSummary.current = registrationSettledSummary(detail, settledSummary.current);
        const credits = await staffBooking.lessonCredits(props.studentId);
        if (credits.portal_student_id !== props.studentId) throw new Error("The refreshed credits did not match this student. Retry this same sale.");
        if (warning?.retryable) throw new Error(warning.message);
        setResult({ description, balance: credits.total_available_credits, warning: warning?.message ?? null, settled: settledSummary.current });
      } finally { props.onBusyChange?.(false); }
    }} />;
}
